import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import {
  CreditCard,
  FolderKanban,
  Home,
  LayoutGrid,
  LogOut,
  Newspaper,
  User,
} from 'lucide-react';
import UserMenu from './UserMenu';
import { useModal } from '../context/ModalProvider';
import { useAuth } from '../context/AuthProvider';
import {
  cyberGlassBackdrop,
  cyberGlassHairline,
  cyberGlassSurface,
  cyberGlassTextPrimary,
} from '../styles/cyberGlass';
import { gradientPillCss } from '../styles/gradientPill';

const NAV_YELLOW = '#E0A15E';

const Bar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: calc(72px + env(safe-area-inset-top));
  padding: env(safe-area-inset-top) 1rem 0;
  background: rgb(35 35 35 / 0%);
  backdrop-filter: saturate(180%) blur(14px);
  -webkit-backdrop-filter: saturate(180%) blur(14px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.02);
  color: ${NAV_YELLOW};
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 30;
`;

const BrandLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: inherit;
  font-weight: 800;
`;

const BrandImg = styled.img`
  height: 48px;
  width: auto;
  display: block;
  border-radius: 6px;
  object-fit: contain;
`;

const DesktopLinks = styled.ul`
  list-style: none;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 0;
  padding: 0;

  @media (max-width: 860px) {
    display: none;
  }
`;

const TopLink = styled(NavLink)`
  text-decoration: none;
  color: ${NAV_YELLOW};
  font-weight: 600;
  opacity: 1;

  &.active {
    opacity: 1;
  }
`;

const NavAction = styled.button`
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: ${NAV_YELLOW};
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-weight: 700;
  cursor: pointer;
`;

const RightCluster = styled.div`
  display: none;
  align-items: center;
  gap: 0.35rem;

  @media (max-width: 860px) {
    display: flex;
  }
`;

const Burger = styled.button<{ $open: boolean }>`
  display: none;
  border: 0;
  background: transparent;
  color: ${NAV_YELLOW};
  width: 34px;
  height: 28px;
  cursor: pointer;
  position: relative;

  span,
  span::before,
  span::after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 3px;
    background: currentColor;
    transition: transform 0.2s ease, top 0.2s ease, opacity 0.2s ease;
  }

  span::before {
    top: -8px;
  }

  span::after {
    top: 8px;
  }

  ${({ $open }) =>
    $open
      ? `
    span { opacity: 0; }
    span::before { top: 0; transform: rotate(45deg); }
    span::after { top: 0; transform: rotate(-45deg); }
  `
      : ''}

  @media (max-width: 860px) {
    display: block;
  }
`;

const Backdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  ${cyberGlassBackdrop};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
  z-index: 35;

  @media (min-width: 861px) {
    display: none;
  }
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: min(360px, 92vw);
  height: 100dvh;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  border-radius: 24px 0 0 24px;
  color: ${cyberGlassTextPrimary};
  box-shadow: -20px 0 48px rgba(3, 4, 6, 0.38);
  overflow: hidden;
  ${cyberGlassSurface};
  transform: translateX(${({ $open }) => ($open ? '0' : '104%')});
  transition: transform 0.22s ease;
  z-index: 40;

  @media (min-width: 861px) {
    display: none;
  }
`;

const DrawerScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: calc(88px + env(safe-area-inset-top)) 1rem calc(1rem + env(safe-area-inset-bottom));
`;

const DrawerContent = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DrawerClose = styled.button`
  position: absolute;
  top: calc(env(safe-area-inset-top) + 1rem);
  right: 1rem;
  width: 36px;
  height: 36px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #f5f7fb;
  display: grid;
  place-items: center;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  touch-action: manipulation;
  pointer-events: auto;
  z-index: 3;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.42);

  &:hover {
    color: ${NAV_YELLOW};
  }
`;

const DrawerList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.7rem;
`;

const DrawerLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  color: ${cyberGlassTextPrimary};
  border: 1px solid ${cyberGlassHairline};
  background: rgba(9, 14, 34, 0.28);
  border-radius: 14px;
  padding: 0.9rem 1rem;
  font-weight: 700;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;

  &.active {
    background: rgba(18, 28, 64, 0.62);
    border-color: rgba(224, 161, 94, 0.38);
    color: #f5f7fb;
  }

  &:hover {
    background: rgba(18, 28, 64, 0.5);
    border-color: rgba(224, 161, 94, 0.36);
    transform: translateY(-1px);
  }
`;

const DrawerFooter = styled.div`
  margin-top: auto;
  display: grid;
  gap: 0.75rem;
  padding-top: 1rem;
`;

const LinkIcon = styled.span`
  width: 1.15rem;
  height: 1.15rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  flex: 0 0 auto;

  svg {
    width: 100%;
    height: 100%;
    stroke-width: 1.85;
  }
`;

const DrawerAuthGroup = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const DrawerPrimaryCta = styled.button<{ disabled?: boolean }>`
  width: 100%;
  text-transform: uppercase;
  ${gradientPillCss};
`;

const DrawerUtilityButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  width: 100%;
  border: 1px solid ${cyberGlassHairline};
  background: rgba(9, 14, 34, 0.28);
  color: ${cyberGlassTextPrimary};
  border-radius: 14px;
  padding: 0.85rem 1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;

  &:hover {
    background: rgba(18, 28, 64, 0.5);
    border-color: rgba(224, 161, 94, 0.36);
    transform: translateY(-1px);
  }
`;

const navItems = [
  { label: 'Home', to: '/', icon: Home },
  { label: 'Services', to: '/services', icon: LayoutGrid },
  { label: 'Works', to: '/works', icon: FolderKanban },
  { label: 'Publications', to: '/publications', icon: Newspaper }
] as const;

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { open } = useModal();
  const {
    user,
    role,
    logout,
    isPrimaryAccountHolder,
    hasOrganizationMembership,
  } = useAuth();
  const desktopNavItems = useMemo(() => navItems.filter((item) => item.to !== '/'), []);
  const drawerNavItems = useMemo(() => navItems, []);
  const hasClientWorkspaceAccess = isPrimaryAccountHolder || hasOrganizationMembership;
  const closeDrawer = () => setDrawerOpen(false);

  useEffect(() => {
    if (!drawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  return (
    <>
      <Bar>
        <BrandLink to="/" aria-label="Cybard home">
          <BrandImg src="/images/CybardLogo1.png" alt="" />
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
            CyBard
          </span>
        </BrandLink>

        <DesktopLinks>
          {desktopNavItems.map((item) => (
            <li key={item.to}>
              <TopLink to={item.to}>
                <span>{item.label}</span>
              </TopLink>
            </li>
          ))}
          <li>
            {user ? (
              <UserMenu />
            ) : (
              <NavAction type="button" onClick={() => open('auth')}>
                Login
              </NavAction>
            )}
          </li>
        </DesktopLinks>

        <RightCluster>
          <Burger
            type="button"
            $open={drawerOpen}
            onClick={() => setDrawerOpen((openValue) => !openValue)}
            aria-label="Toggle menu"
          >
            <span />
          </Burger>
        </RightCluster>
      </Bar>

      <Backdrop
        type="button"
        $open={drawerOpen}
        aria-label="Close menu"
        onClick={closeDrawer}
      />

      <Drawer $open={drawerOpen}>
        <DrawerClose
          type="button"
          aria-label="Close menu"
          onClick={(event) => {
            event.stopPropagation();
            closeDrawer();
          }}
        >
          X
        </DrawerClose>
        <DrawerScrollArea>
          <DrawerContent>
            <DrawerList>
              {drawerNavItems.map((item) => (
                <li key={item.to}>
                  <DrawerLink to={item.to} end={item.to === '/'} onClick={closeDrawer}>
                    <LinkIcon aria-hidden="true">
                      <item.icon />
                    </LinkIcon>
                    <span>{item.label}</span>
                  </DrawerLink>
                </li>
              ))}
            </DrawerList>

            <DrawerFooter>
              {user ? (
                <DrawerAuthGroup>
                  <DrawerLink to="/dashboard/profile" onClick={closeDrawer}>
                    <LinkIcon aria-hidden="true">
                      <User />
                    </LinkIcon>
                    <span>Account</span>
                  </DrawerLink>
                  {role === 'client' && hasClientWorkspaceAccess ? (
                    <>
                      <DrawerLink to="/dashboard/projects" onClick={closeDrawer}>
                        <LinkIcon aria-hidden="true">
                          <FolderKanban />
                        </LinkIcon>
                        <span>Projects</span>
                      </DrawerLink>
                      <DrawerLink to="/dashboard/billing" onClick={closeDrawer}>
                        <LinkIcon aria-hidden="true">
                          <CreditCard />
                        </LinkIcon>
                        <span>Billing</span>
                      </DrawerLink>
                    </>
                  ) : null}
                  <DrawerUtilityButton
                    type="button"
                    onClick={async () => {
                      closeDrawer();
                      try {
                        await logout();
                      } catch {
                        // noop
                      }
                    }}
                  >
                    <LinkIcon aria-hidden="true">
                      <LogOut />
                    </LinkIcon>
                    Logout
                  </DrawerUtilityButton>
                </DrawerAuthGroup>
              ) : (
                <DrawerPrimaryCta
                  type="button"
                  onClick={() => {
                    closeDrawer();
                    open('auth');
                  }}
                >
                  Login
                </DrawerPrimaryCta>
              )}
            </DrawerFooter>
          </DrawerContent>
        </DrawerScrollArea>
      </Drawer>
    </>
  );
}
